import React, { useState, useMemo } from 'react';
import { X, Search, ArrowUpDown, ChevronDown, RefreshCw } from 'lucide-react';

export const SelectionModal = ({
  isOpen,
  onClose,
  title,
  items = [],
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortOptions = [],
  onSelect,
  renderItem,
  searchPlaceholder,
  emptyMessage,
  noItemsMessage,
  getItemKey,
  filterItems,
  onRefresh,
  isRefreshing = false,
  refreshTitle = 'Refresh list',
}) => {
  const [isSortOpen, setIsSortOpen] = useState(false);

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || 'Sort by';
  };

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery || typeof searchQuery !== 'string') {
      return items || [];
    }
    
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return items || [];
    }
    
    // Use custom filter function if provided, otherwise use default search
    if (filterItems) {
      return filterItems(items, query);
    }
    
    // Default search - search in common fields
    return (items || []).filter(item => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      
      const searchInField = (field) => {
        if (field == null) return false;
        if (typeof field === 'string' && field.trim() === '') return false;
        if (typeof field === 'number') {
          return String(field).toLowerCase().includes(query);
        }
        const fieldStr = String(field).toLowerCase().trim();
        return fieldStr.length > 0 && fieldStr.includes(query);
      };
      
      // Search in common fields
      return searchInField(item.nombre) ||
             searchInField(item.name) ||
             searchInField(item.title) ||
             searchInField(item.id);
    });
  }, [items, searchQuery, filterItems]);

  // Sort filtered items
  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return (a.nombre || a.name || a.title || '').localeCompare(b.nombre || b.name || b.title || '');
        case 'name-desc':
          return (b.nombre || b.name || b.title || '').localeCompare(a.nombre || a.name || a.title || '');
        case 'date-asc':
          return new Date(a.date_created || 0) - new Date(b.date_created || 0);
        case 'date-desc':
          return new Date(b.date_created || 0) - new Date(a.date_created || 0);
        default:
          return 0;
      }
    });
  }, [filteredItems, sortBy]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-0 flex items-center justify-center z-[90] animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4 animate-modalEnter">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <button 
            onClick={() => {
              onClose();
              setSearchQuery('');
            }} 
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex gap-2 mb-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border-container rounded-full"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {onRefresh && (
            <button
              type="button"
              onClick={(e) => onRefresh(e)}
              disabled={isRefreshing}
              title={refreshTitle}
              className="relative z-20 shrink-0 flex items-center justify-center p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
              aria-label={refreshTitle}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}

          {/* Custom Sort Dropdown */}
          {sortOptions.length > 0 && (
            <div className="relative button-no-hover-effect">
              <button
                className="flex items-center gap-2 px-2 pr-0 py-2 rounded-full"
                onClick={() => setIsSortOpen(!isSortOpen)}
              >
                <ArrowUpDown className="w-4 h-4" />
                <span>{getSortLabel()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isSortOpen && (
                <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        sortBy === option.value ? 'text-primary' : 'text-gray-700'
                      }`}
                      onClick={() => {
                        setSortBy(option.value);
                        setIsSortOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="max-h-[60vh] min-h-[60vh] overflow-y-auto">
          {items && items.length > 0 ? (
            <div className="space-y-0">
              {sortedItems.length > 0 ? (
                sortedItems.map((item, index) => {
                  const key = getItemKey ? getItemKey(item, index) : (item.id || index);
                  return (
                    <div 
                      key={key} 
                      className='p-3 px-0 hover:bg-gray-100 cursor-pointer border-bottom-gray text-left'
                      onClick={() => onSelect(item)}
                    >
                      {renderItem ? renderItem(item) : (
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <p className="font-medium">{item.nombre || item.name || item.title || 'N/A'}</p>
                            {item.pais && (
                              <p className="text-sm text-gray-500">{item.pais}</p>
                            )}
                          </div>
                          {item.nif && (
                            <span className="text-xs bg-bgGray px-3 py-1 rounded-full">
                              {item.nif}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {emptyMessage || 'No items found'} "{searchQuery}"
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className='opacity-75 mb-4'>
                {noItemsMessage || 'No items available'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
